import type { TranslationKey } from '../../i18n/he';

/*
 * רמות קושי.
 *
 * **אף רמה אינה מרמה** — אין בונוס משאבים, אין יחידות זולות יותר, ואין ראייה
 * שהשחקן לא מקבל. כל ההבדל בין הרמות הוא בכמה טוב ה-AI **משחק**, וכל שדה כאן
 * הוא החלטה שגם שחקן אנושי מקבל: כמה קוצרים לקנות, כמה עמוק למלא את התור, כמה
 * מבנים להרשות לעצמו, האם לתקן מבנה פגוע, והאם לשלוח כלב סיור.
 *
 * ‏`productionPct` הוסר: הוא היה 100 בכל שלוש הרמות מאז שנכתב, כלומר קוד מת
 * שנראה כמו דוושת רמאות ממתינה. אם אי פעם יידרש בונוס אמיתי — הוא ייכתב כאן
 * כמספר גלוי ויוצג לשחקן, ולא יוסתר בתוך הקוד.
 */

export interface DifficultyDef {
    readonly id: string;
    readonly nameKey: TranslationKey;
    /** כל כמה טיקים ה-AI מקבל החלטות. נדיר יותר = איטי יותר בתגובה. */
    readonly decisionIntervalTicks: number;
    /** כמה יחידות קרב הוא צובר לפני שהוא שולח גל התקפה. */
    readonly waveSize: number;
    /**
     * הטיק הראשון שבו הוא מוכן לצאת להתקפה.
     *
     * **בלי זה אין משחק, יש קרב.** ה-AI היה שולח את כוח הפתיחה שלו בשנייה
     * השלישית, לפני שלשחקן היה זמן לבנות משהו. ב-RA2 היריב בונה כלכלה קודם,
     * וההתקפה הראשונה מגיעה אחרי כמה דקות. הגנה על הבסיס אינה מושהית — היא
     * תמיד מיידית.
     */
    readonly firstAttackTick: number;
    /** אחוז ההחלטות שהוא פשוט מדלג עליהן. זה מה שהופך רמה קלה לקלה. */
    readonly mistakePct: number;
    /**
     * כמה קוצרים הוא שואף להחזיק.
     *
     * **זו ההחלטה הכלכלית היחידה שבאמת משנה סדר גודל**, וכל שחקן אנושי מקבל
     * אותה בעצמו: עוד קוצר הוא עוד הכנסה, ומזה עוד צבא ועוד מבנים. יריב קשה
     * שמחזיק שישה קוצרים אינו מקבל בונוס — הוא פשוט קונה אותם.
     */
    readonly harvesters: number;
    /** כמה פריטים לשמור בכל תור ייצור. עמוק יותר = פחות זמן מת בין החלטות. */
    readonly queueDepth: number;
    /** כמה יחידות נשארות לשמור על הבסיס ואינן יוצאות בגל. */
    readonly homeGuard: number;
    /**
     * כמה כלבי סיור להחזיק. **זו כל ה"חוכמה" של היריב**: בלי סיור הוא מכיר רק
     * את הכיוון שאליו שלח גלים, ולכן שקלול היעדים שלו חסר משמעות. ראו
     * `ai/brain.ts`.
     */
    readonly scouts: number;
    /** האם הוא מתקן מבנים פגועים. שחקן אנושי מתקן; יריב קל לא טורח. */
    readonly repairs: boolean;
    /** כל כמה טיקים הגל גדל ביחידה, ועד כמה יחידות תוספת. */
    readonly waveGrowthTicks: number;
    readonly maxWaveBonus: number;
    /**
     * תוספת לתקרת הבנייה — כמה מכל סוג הוא מרשה לעצמו מעבר לבסיס.
     *
     * מופרד בין כלכלה להגנה בכוונה: **תוספת אחידה מחזירה את ההתבצרות**
     * (מלכודת 31), כי מגדלים זולים מטנקים וכל שקל היה זורם אליהם. ההרחבה
     * הגדולה שייכת למה שמייצר, לא למה שעומד.
     */
    readonly economyBonus: number;
    readonly defenseBonus: number;
}

export const DIFFICULTY_DEFS = [
    {
        id: 'easy',
        nameKey: 'difficulty_easy',
        decisionIntervalTicks: 60,
        waveSize: 6,
        // חמש דקות עד ההתקפה הראשונה — מספיק זמן לבנות בסיס בנחת וללמוד.
        firstAttackTick: 6000,
        mistakePct: 45,
        harvesters: 3,
        queueDepth: 3,
        homeGuard: 2,
        scouts: 0,
        repairs: false,
        waveGrowthTicks: 3000,
        maxWaveBonus: 3,
        economyBonus: 0,
        defenseBonus: 0,
    },
    {
        id: 'normal',
        nameKey: 'difficulty_normal',
        decisionIntervalTicks: 30,
        waveSize: 4,
        firstAttackTick: 4200,
        mistakePct: 15,
        harvesters: 4,
        queueDepth: 4,
        homeGuard: 2,
        scouts: 1,
        repairs: true,
        waveGrowthTicks: 2400,
        maxWaveBonus: 5,
        economyBonus: 0,
        defenseBonus: 0,
    },
    {
        id: 'hard',
        nameKey: 'difficulty_hard',
        decisionIntervalTicks: 15,
        /*
         * **גל פתיחה קטן ומהיר, שגדל הרבה יותר.** הקטן-והמוקדם הוא הטרדה שלא
         * נותנת לשחקן לבנות בשקט; מה שהופך את הרמה לקשה **באמת** הוא שהגל
         * ממשיך לגדול עד 15 יחידות במקום 8, וכל 60 שניות ולא כל 120.
         */
        waveSize: 3,
        firstAttackTick: 2400,
        mistakePct: 0,
        /*
         * שישה קוצרים ושמונה מקומות בתור — כלומר כלכלה שמסוגלת לממן את הגלים
         * שלמעלה. בלי זה התקרות והגלים הם מספרים שהיריב לא מגיע אליהם
         * (מלכודת 67: שינוי שלא נמדד כמשפיע כי לא היו התנאים שבהם הוא נושך).
         */
        harvesters: 6,
        queueDepth: 6,
        homeGuard: 4,
        scouts: 2,
        repairs: true,
        waveGrowthTicks: 1200,
        maxWaveBonus: 12,
        economyBonus: 2,
        defenseBonus: 1,
    },
] as const satisfies readonly DifficultyDef[];

export type DifficultyName = (typeof DIFFICULTY_DEFS)[number]['id'];
export type DifficultyId = number;

export const DIFFICULTY_BY_NAME: Readonly<Record<DifficultyName, DifficultyId>> = Object.fromEntries(
    DIFFICULTY_DEFS.map((def, index) => [def.id, index]),
) as Record<DifficultyName, DifficultyId>;

export function difficultyDef(id: DifficultyId): DifficultyDef {
    const def = DIFFICULTY_DEFS[id];
    if (!def) throw new Error(`רמת קושי לא מוכרת: ${id}`);
    return def;
}

export const DEFAULT_DIFFICULTY: DifficultyId = DIFFICULTY_BY_NAME.normal;
